/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Pending group-join-request banner (owner-approval invite flow). Shown above
// the message timeline in the 1-1 chat where a `com.ping.group_join_request`
// arrived: "<name> wants to join <group>" + Approve / Dismiss. Approve runs the
// host's `addWebGroupMembers`; Dismiss hides it locally. Tailwind-first.

import { Check, UserPlus, X } from 'lucide-react-native';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';

import { ProfileAvatar } from '../profile/ProfileParts';

export interface GroupJoinRequestBannerProps {
  /** Display name of the person requesting to join. */
  readonly requesterName: string;
  /** Avatar of the requester, if resolved. */
  readonly requesterAvatarUri?: string | undefined;
  /** Group name, if the approver can resolve it locally. */
  readonly groupName?: string | undefined;
  /** Approve → add the requester to the group. */
  readonly onApprove: () => void;
  /** Dismiss the request locally (no add). */
  readonly onDismiss: () => void;
  /** True while the add is in flight (disables the buttons + spins Approve). */
  readonly busy?: boolean | undefined;
}

export function GroupJoinRequestBanner({
  requesterName,
  requesterAvatarUri,
  groupName,
  onApprove,
  onDismiss,
  busy = false,
}: GroupJoinRequestBannerProps): JSX.Element {
  const groupLabel = groupName !== undefined && groupName.length > 0 ? groupName : 'your group';
  return (
    <View className="mx-3 mt-2 flex-row items-center gap-3 rounded-xl border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel px-3 py-2.5">
      <View className="relative">
        <ProfileAvatar name={requesterName} uri={requesterAvatarUri} sizeClassName="w-9 h-9 rounded-full" />
        <View className="absolute -bottom-0.5 -right-0.5 h-4 w-4 items-center justify-center rounded-full bg-light-accent-brand dark:bg-dark-accent-brand">
          <UserPlus size={10} strokeWidth={2.5} color="#FFFFFF" />
        </View>
      </View>
      <View className="flex-1 min-w-0">
        <Text
          className="text-[14px] text-light-text-primary dark:text-dark-text-primary"
          numberOfLines={2}
        >
          <Text className="font-bold">{requesterName}</Text>
          {' wants to join '}
          <Text className="font-bold">{groupLabel}</Text>
        </Text>
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Dismiss request"
        onPress={onDismiss}
        disabled={busy}
        className="h-8 w-8 items-center justify-center rounded-full active:opacity-70"
      >
        <X size={16} color="#B5BAC3" />
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Add ${requesterName} to the group`}
        onPress={onApprove}
        disabled={busy}
        className={`h-8 flex-row items-center gap-1.5 rounded-full bg-light-accent-brand dark:bg-dark-accent-brand px-3 active:opacity-80 ${busy ? 'opacity-60' : ''}`}
      >
        {busy ? (
          <ActivityIndicator size="small" color="#FFFFFF" />
        ) : (
          <Check size={14} strokeWidth={2.5} color="#FFFFFF" />
        )}
        <Text className="text-[13px] font-bold text-white">Add</Text>
      </Pressable>
    </View>
  );
}
